import type { AllFilters, ClaimsTableColumnSetting } from '../../types/claims';
import type { OrderBy } from '../../stores/ClaimsStore';
import { readTenantFilters, writeTenantFilters } from './filtersTenantStorage';

// One localStorage blob for the Claims page: the search box, "Show missing
// claims", column filters, sort, selected saved view and column settings
// (visibility + pin). Stored per tenant and user, see filtersTenantStorage.
export const CLAIMS_FILTERS_STORAGE_KEY = 'filters:claims';

export interface PersistedClaimsFilters {
  claimKeywordSearch: string;
  showMissingClaims: boolean;
  allFilters: AllFilters | null;
  /** undefined = nothing saved (keep the default sort); null = sorting cleared. */
  orderBy?: OrderBy | null;
  selectedFilterView: string | null;
  columnSettings: ClaimsTableColumnSetting[] | null;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string');

// Column filters are either a plain date range (string[]) or { operator, values }.
const parseAllFilters = (value: unknown): AllFilters | null => {
  if (!isPlainObject(value)) return null;
  const entries = Object.entries(value).filter(([, filter]) => {
    if (isStringArray(filter)) return filter.length > 0;
    return (
      isPlainObject(filter) &&
      (filter.operator === 'and' || filter.operator === 'or' || filter.operator === 'isnot') &&
      isStringArray(filter.values) &&
      filter.values.length > 0
    );
  });
  return entries.length > 0 ? (Object.fromEntries(entries) as AllFilters) : null;
};

const parseOrderBy = (value: unknown): OrderBy | null | undefined => {
  if (value === null) return null;
  if (
    isPlainObject(value) &&
    typeof value.field === 'string' &&
    value.field &&
    (value.direction === 'asc' || value.direction === 'desc')
  ) {
    return { field: value.field, direction: value.direction };
  }
  return undefined;
};

export const loadPersistedClaimsFilters = (scope: string | null): PersistedClaimsFilters => {
  const saved = readTenantFilters(CLAIMS_FILTERS_STORAGE_KEY, scope);
  const blob = isPlainObject(saved) ? saved : {};

  return {
    claimKeywordSearch: typeof blob.claimKeywordSearch === 'string' ? blob.claimKeywordSearch : '',
    showMissingClaims: blob.showMissingClaims === true,
    allFilters: parseAllFilters(blob.allFilters),
    orderBy: parseOrderBy(blob.orderBy),
    selectedFilterView: typeof blob.selectedFilterView === 'string' ? blob.selectedFilterView : null,
    columnSettings: Array.isArray(blob.columnSettings) ? (blob.columnSettings as ClaimsTableColumnSetting[]) : null,
  };
};

export const savePersistedClaimsFilters = (scope: string | null, filters: PersistedClaimsFilters): void => {
  writeTenantFilters(CLAIMS_FILTERS_STORAGE_KEY, scope, filters);
};
